import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { trackHomeEvent } from "../../lib/homeAnalytics";
import { localDateKey } from "../../lib/reflectionDrip";
import { cardMotionLarge } from "./motionConstants";
import type { ReflectionContent } from "../../hooks/useReflectionDrip";

// Own key, separate from the drip's own storage — dedupes the "shown"
// analytics event per local day + space so navigating away and back (or a
// stray re-render) doesn't re-fire it and inflate the shown→answered funnel.
const SHOWN_STORAGE_KEY = "home_reflection_shown_v1";

const ReflectionCard = ({
  content, onAnswer, onSkip,
}: {
  content: ReflectionContent;
  onAnswer: (text: string) => Promise<"insight" | "action">;
  onSkip: () => void;
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedType, setSavedType] = useState<"insight" | "action">("insight");

  useEffect(() => {
    const marker = `${localDateKey()}:${content.spaceId}`;
    let last: string | null = null;
    try {
      last = localStorage.getItem(SHOWN_STORAGE_KEY);
    } catch {}
    if (last !== marker) {
      trackHomeEvent("reflection_shown", { area: content.spaceName, day_index: content.index });
      try {
        localStorage.setItem(SHOWN_STORAGE_KEY, marker);
      } catch {}
    }
  }, [content.spaceId, content.spaceName, content.index]);

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      const type = await onAnswer(trimmed);
      setSavedType(type);
      setSaved(true);
    } catch {
      toast.error(t("action_save_error", "Something went wrong"));
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <motion.div {...cardMotionLarge} className="rounded-2xl border border-primary/15 bg-gradient-to-br from-card to-primary/[0.03] p-5">
        <p className="text-sm text-foreground">
          {t(savedType === "action" ? "home_reflection_saved_action" : "home_reflection_saved")}
        </p>
        {content.next && (
          <p className="text-xs text-muted-foreground mt-1.5">
            {content.next.identityLabel
              ? t("home_reflection_tomorrow_identity", { area: content.next.name, identity: content.next.identityLabel })
              : t("home_reflection_tomorrow", { area: content.next.name })}
          </p>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      {...cardMotionLarge}
      className="relative rounded-2xl border bg-card p-5 md:p-6 overflow-hidden"
      style={{ borderColor: `${content.spaceColor}33` }}
    >
      <span className="absolute inset-y-0 start-0 w-[3px]" style={{ background: content.spaceColor }} />
      <div className="flex items-center gap-2 mb-2.5">
        <span className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: content.spaceColor }}>
          {content.spaceName}
        </span>
        <span className="text-[11px] text-muted-foreground/60">
          {t("home_reflection_counter", { index: content.index, total: content.total })}
        </span>
        <button
          onClick={onSkip}
          className="ms-auto -my-2 inline-flex min-h-[44px] min-w-[44px] items-center justify-center px-2 text-xs text-muted-foreground/60 hover:text-muted-foreground"
        >
          {t("home_reflection_skip")}
        </button>
      </div>
      {content.framing && (
        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 mb-1.5">{content.framing}</p>
      )}
      <p className="text-[15px] font-semibold text-foreground leading-snug mb-3">
        {content.question ?? content.framing}
      </p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        placeholder={t("home_reflection_placeholder")}
        aria-label={t("home_reflection_placeholder")}
        className={cn(
          "w-full resize-none rounded-xl border border-border/70 bg-background/60 px-3.5 py-2.5",
          "text-sm text-foreground placeholder:text-muted-foreground/50",
          "focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40"
        )}
      />
      <div className="flex items-center justify-between mt-2.5">
        {content.next ? (
          <p className="text-[11.5px] text-muted-foreground/60">
            {content.next.identityLabel
              ? t("home_reflection_tomorrow_identity", { area: content.next.name, identity: content.next.identityLabel })
              : t("home_reflection_tomorrow", { area: content.next.name })}
          </p>
        ) : <span />}
        <Button size="sm" className="rounded-xl px-5 min-h-[44px]" disabled={!text.trim() || saving} onClick={submit}>
          {t("home_reflection_cta")}
        </Button>
      </div>
    </motion.div>
  );
};

export default ReflectionCard;
